import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import UpdateIcon from '@mui/icons-material/Update';
import { RootState } from '../store';

const PWAUpdateNotifier = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [serviceWorkerRegistration, setServiceWorkerRegistration] = useState<ServiceWorkerRegistration | null>(null);
  
  // Redux에서 사용자 로그인 상태 가져오기
  const user = useSelector((state: RootState) => state.auth.user);
  
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r: ServiceWorkerRegistration | undefined) {
      console.log('SW Registered: ' + r);
      setServiceWorkerRegistration(r || null);
    },
    onRegisterError(error: any) {
      console.log('SW registration error', error);
    },
    onNeedRefresh() {
      console.log('New version available, need refresh');
      setUpdateAvailable(true);
    },
    onOfflineReady() {
      console.log('App ready to work offline');
    },
  });

  // 로그인 시 업데이트 확인
  useEffect(() => {
    if (user && serviceWorkerRegistration) {
      console.log('User logged in, checking for updates...');
      serviceWorkerRegistration.update();
    }
  }, [user, serviceWorkerRegistration]);

  // 업데이트 감지 시 처리
  useEffect(() => {
    if (needRefresh) {
      // 개발 환경에서는 즉시 업데이트, 프로덕션에서는 사용자에게 알림
      if (import.meta.env.DEV) {
        handleUpdate();
      } else {
        setUpdateAvailable(true);
      }
    }
  }, [needRefresh]);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await updateServiceWorker(true);
      // 업데이트 후 페이지 새로고침
      window.location.reload();
    } catch (error: any) {
      console.error('Update failed:', error);
      setIsUpdating(false);
    }
  };

  const handleSkip = () => {
    setUpdateAvailable(false);
    setOfflineReady(false);
    setNeedRefresh(false);
  };

  // 자동 업데이트 (프로덕션 환경에서도 자동화하려면 이 부분 활성화)
  /*
  useEffect(() => {
    if (needRefresh) {
      // 5초 후 자동 업데이트
      const timer = setTimeout(() => {
        handleUpdate();
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [needRefresh]);
  */

  if (!updateAvailable && !offlineReady) {
    return null;
  }

  return (
    <>
      {/* 업데이트 다이얼로그 */}
      <Dialog 
        open={updateAvailable} 
        onClose={handleSkip}
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UpdateIcon color="primary" />
          새 버전 업데이트
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            새로운 버전이 사용 가능합니다. 최신 기능과 개선사항을 이용하시려면 업데이트해주세요.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            업데이트 후 앱이 자동으로 새로고침됩니다.
          </Typography>
          {isUpdating && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">업데이트 중...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSkip} disabled={isUpdating}>
            나중에
          </Button>
          <Button 
            onClick={handleUpdate} 
            variant="contained" 
            startIcon={<RefreshIcon />}
            disabled={isUpdating}
          >
            지금 업데이트
          </Button>
        </DialogActions>
      </Dialog>

      {/* 오프라인 준비 완료 알림 */}
      <Dialog 
        open={offlineReady} 
        onClose={() => setOfflineReady(false)}
        maxWidth="sm"
      >
        <DialogTitle>오프라인 준비 완료</DialogTitle>
        <DialogContent>
          <Typography>
            앱이 오프라인에서도 사용할 수 있도록 준비되었습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfflineReady(false)}>확인</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PWAUpdateNotifier;
